/**
 * Calculation Methodology Component
 * Shows detailed explanations of how each metric was calculated
 * Designed for forensic accountants who need to understand the methodology
 */

import React, { useState } from 'react';

interface MethodologyItem {
  title: string;
  value: string | number;
  formula: string;
  explanation: string;
  example?: string;
  dataSource?: string;
}

interface CalculationMethodologyProps {
  section: 'variable_profit' | 'growth_adjustment' | 'final_calculation';
  data: any;
}

export default function CalculationMethodology({ section, data }: CalculationMethodologyProps) {
  const [expandedItem, setExpandedItem] = useState<string | null>(null);

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (value?: number) => {
    if (value === undefined || value === null) return 'N/A';
    return `${value.toFixed(2)}%`;
  };
  
  const safeValue = (value?: number) => {
    return (value !== undefined && value !== null) ? value : 0;
  };

  // Define methodology for each section
  const methodologies: Record<string, MethodologyItem[]> = {
    variable_profit: [
      {
        title: 'Variable Margin',
        value: formatPercent(data?.variable_margin_pct),
        formula: '(Revenue - Variable Expenses) / Revenue × 100',
        explanation: 'STEP 1: The percentage of revenue that becomes variable profit. This is calculated FIRST because it\'s used to determine both Benchmark and Coverage VP. This metric shows the efficiency of operations.',
        example: (data?.benchmark_revenue && data?.benchmark_variable_expenses && data?.variable_margin_pct)
          ? `Revenue: ${formatCurrency(data.benchmark_revenue)}\nVariable Expenses: ${formatCurrency(data.benchmark_variable_expenses)}\nVariable Profit: ${formatCurrency(data.benchmark_revenue - data.benchmark_variable_expenses)}\nVariable Margin: (${formatCurrency(data.benchmark_revenue - data.benchmark_variable_expenses)} / ${formatCurrency(data.benchmark_revenue)}) × 100 = ${formatPercent(data.variable_margin_pct)}`
          : 'Calculation: (Revenue - Variable Expenses) / Revenue × 100',
        dataSource: 'Benchmark period financial data'
      },
      {
        title: 'Benchmark VP',
        value: formatCurrency(data?.benchmark_vp),
        formula: 'Benchmark Revenue - Variable Expenses',
        explanation: 'STEP 2: The Variable Profit during a "normal" comparison period, typically the same months from a prior year. This represents what the business would have earned under normal operating conditions.',
        example: (data?.benchmark_revenue && data?.benchmark_variable_expenses && data?.benchmark_vp) 
          ? `Revenue: ${formatCurrency(data.benchmark_revenue)}\nVariable Expenses: ${formatCurrency(data.benchmark_variable_expenses)}\nBenchmark VP: ${formatCurrency(data.benchmark_vp)}`
          : 'Calculation: Benchmark Revenue - Variable Expenses during the selected comparison period',
        dataSource: 'P&L statements from benchmark period'
      },
      {
        title: 'Coverage VP',
        value: formatCurrency(data?.coverage_vp),
        formula: 'Coverage Revenue - Variable Expenses',
        explanation: 'STEP 3: The actual Variable Profit during the interruption period. This shows what the business actually earned while operations were disrupted.',
        example: (data?.coverage_revenue && data?.coverage_variable_expenses && data?.coverage_vp)
          ? `Revenue: ${formatCurrency(data.coverage_revenue)}\nVariable Expenses: ${formatCurrency(data.coverage_variable_expenses)}\nCoverage VP: ${formatCurrency(data.coverage_vp)}`
          : 'Calculation: Coverage Revenue - Variable Expenses during the interruption period',
        dataSource: 'P&L statements from coverage period'
      },
      {
        title: 'VP Loss',
        value: formatCurrency(data?.vp_loss),
        formula: 'Benchmark VP - Coverage VP',
        explanation: 'STEP 4 (RESULT): The direct loss in Variable Profit caused by the business interruption. This is the core calculation of Step 1 in the Standard Business Interruption (BI) methodology.',
        example: (data?.benchmark_vp && data?.coverage_vp && data?.vp_loss)
          ? `Benchmark VP: ${formatCurrency(data.benchmark_vp)}\nCoverage VP: ${formatCurrency(data.coverage_vp)}\nVP Loss: ${formatCurrency(data.vp_loss)}`
          : 'Calculation: Benchmark VP - Coverage VP',
        dataSource: 'Calculated from benchmark and coverage periods'
      }
    ],
    growth_adjustment: [
      {
        title: 'Claimant Factor',
        value: formatPercent(data?.claimant_factor_pct),
        formula: 'Linear regression of historical revenue trends',
        explanation: 'STEP 1: The business-specific growth or decline trend based on historical performance. Calculated using regression analysis of 3-4 years of pre-event revenue data. A negative value indicates the business was declining before the interruption.',
        example: data?.historical_revenues && data.historical_revenues.length > 0
          ? `Historical revenue trend analysis:\n${data.historical_revenues.map((item: any, idx: number) => {
              const yearLabel = item.year || `Year ${idx + 1}`;
              const revenue = formatCurrency(item.revenue);
              const change = item.change_pct ? ` (${item.change_pct > 0 ? '+' : ''}${item.change_pct.toFixed(2)}%)` : '';
              return `${yearLabel}: ${revenue}${change}`;
            }).join('\n')}\nAverage trend: ${formatPercent(data.claimant_factor_pct)} per year`
          : `Regression analysis of historical revenue data.\nCalculated trend: ${formatPercent(data?.claimant_factor_pct)} per year`,
        dataSource: 'Multi-year historical revenue data'
      },
      {
        title: 'General Factor',
        value: formatPercent(data?.general_factor_pct),
        formula: 'Industry benchmarks or economic indicators',
        explanation: 'STEP 2: Industry-wide or economic growth adjustment affecting all similar businesses. Based on industry benchmarks, GDP growth, or regional economic data. Zero indicates no general market adjustment was applied.',
        example: data?.general_factor_pct === 0 ? 'No industry adjustment applied (stable market or insufficient data)' : `Industry growth rate: ${formatPercent(data?.general_factor_pct)}`,
        dataSource: 'Industry reports, economic data, or comparative analysis'
      },
      {
        title: 'Total Factor',
        value: formatPercent(data?.total_factor_pct),
        formula: 'Claimant Factor + General Factor',
        explanation: 'STEP 3 (SUM): The combined growth/decline adjustment applied to project what should have happened without the interruption. This ensures the claim only covers losses caused by the event, not pre-existing trends.',
        example: `${formatPercent(data?.claimant_factor_pct)} + ${formatPercent(data?.general_factor_pct)} = ${formatPercent(data?.total_factor_pct)}`,
        dataSource: 'Calculated from claimant and general factors'
      },
      {
        title: 'Incremental Profit',
        value: formatCurrency(data?.incremental_lost_profit),
        formula: '(Benchmark Revenue × Total Factor%) × Variable Margin%',
        explanation: 'STEP 4 (RESULT): The dollar impact of growth or decline trends. A negative value means the business would have earned less even without the interruption, so this amount is subtracted from the claim to avoid over-compensation.',
        example: (data?.benchmark_revenue && data?.total_factor_pct !== undefined && data?.but_for_revenue && data?.incremental_revenue && data?.variable_margin_pct && data?.incremental_lost_profit)
          ? `Step 1 - But-For Revenue:\n  ${formatCurrency(data.benchmark_revenue)} × (1 + ${formatPercent(data.total_factor_pct)}) = ${formatCurrency(data.but_for_revenue)}\n\nStep 2 - Incremental Revenue:\n  ${formatCurrency(data.but_for_revenue)} - ${formatCurrency(data.benchmark_revenue)} = ${formatCurrency(data.incremental_revenue)}\n\nStep 3 - Incremental Profit:\n  ${formatCurrency(data.incremental_revenue)} × ${formatPercent(data.variable_margin_pct)} = ${formatCurrency(data.incremental_lost_profit)}`
          : 'Calculation: (Benchmark Revenue × Total Factor%) × Variable Margin%',
        dataSource: 'Calculated from trend factors and variable margin'
      }
    ],
    final_calculation: [
      {
        title: 'Step 1: Variable Profit Loss',
        value: formatCurrency(data?.step1_vp_loss),
        formula: 'Benchmark VP - Coverage VP',
        explanation: 'The direct, unadjusted loss in variable profit during the interruption period. This is the starting point for the claim calculation.',
        dataSource: 'From Variable Profit Analysis'
      },
      {
        title: 'Step 2: Incremental Profit',
        value: formatCurrency(data?.step2_incremental),
        formula: 'Growth/Decline Trend Adjustment',
        explanation: 'Adjustment for business trends that would have occurred regardless of the interruption. Negative values reduce the claim; positive values increase it.',
        dataSource: 'From Growth Adjustment Analysis'
      },
      {
        title: 'Additional Expenses',
        value: formatCurrency(data?.additional_expenses),
        formula: 'Accountant Fees + Continuing Expenses + Extra Expenses',
        explanation: 'Additional costs incurred due to the interruption, including professional fees for claim preparation, continuing expenses during closure, and extra expenses to minimize loss.',
        example: (data?.additional_expenses && data?.additional_expenses > 0) 
          ? `Accountant Fees: ${formatCurrency(safeValue(data?.accountant_fees))}\nContinuing Expenses: ${formatCurrency(safeValue(data?.continuing_expenses))}\nExtra Expenses: ${formatCurrency(safeValue(data?.extra_expenses))}\nTotal: ${formatCurrency(data.additional_expenses)}`
          : 'No additional expenses claimed in this report',
        dataSource: 'Policy parameters and expense documentation'
      },
      {
        title: 'Policy Threshold',
        value: formatCurrency(data?.threshold_amount),
        formula: data?.threshold_type === 'percentage' ? 'Step 1 Loss × Threshold %' : 'Fixed Dollar Amount',
        explanation: 'The policy deductible or threshold that must be met before claim payment begins. This is subtracted from the total loss to determine the final compensable amount.',
        example: (data?.threshold_amount && data?.threshold_amount > 0) 
          ? `Type: ${data?.threshold_type === 'percentage' ? `${data?.threshold_pct}% of Step 1 Loss` : 'Fixed dollar threshold'}\nAmount: ${formatCurrency(data.threshold_amount)}`
          : 'No threshold/deductible applied to this claim',
        dataSource: 'Insurance policy terms'
      }
    ]
  };

  const items = methodologies[section] || [];

  const toggleExpand = (title: string) => {
    setExpandedItem(expandedItem === title ? null : title);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-blue-50 to-blue-50 px-4 py-3 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900 flex items-center">
          <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Calculation Methodology
        </h3>
        <p className="text-xs text-gray-600 mt-1">Click any item to see detailed calculation explanation</p>
      </div>

      <div className="divide-y divide-gray-200">
        {items.map((item) => (
          <div key={item.title} className="hover:bg-gray-50 transition-colors">
            <button
              onClick={() => toggleExpand(item.title)}
              className="w-full px-4 py-3 text-left flex items-center justify-between"
            >
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-gray-900">{item.title}</span>
                  <span className="font-bold text-gray-900">{item.value}</span>
                </div>
                <div className="text-xs text-gray-600 font-mono">{item.formula}</div>
              </div>
              <svg 
                className={`w-5 h-5 ml-3 text-gray-400 transition-transform ${expandedItem === item.title ? 'rotate-180' : ''}`}
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {expandedItem === item.title && (
              <div className="px-4 pb-4 bg-blue-50 border-t border-blue-100">
                <div className="space-y-3 text-sm">
                  {/* Explanation */}
                  <div>
                    <p className="font-medium text-gray-700 mb-1">What this means:</p>
                    <p className="text-gray-600">{item.explanation}</p>
                  </div>

                  {/* Example */}
                  {item.example && (
                    <div>
                      <p className="font-medium text-gray-700 mb-1">Calculation example:</p>
                      <div className="bg-white rounded p-3 font-mono text-xs text-gray-700 whitespace-pre-line border border-blue-200">
                        {item.example}
                      </div>
                    </div>
                  )}

                  {/* Data Source */}
                  {item.dataSource && (
                    <div>
                      <p className="font-medium text-gray-700 mb-1">Data source:</p>
                      <p className="text-gray-600 italic">{item.dataSource}</p>
                    </div>
                  )}

                  {/* Professional Note */}
                  <div className="bg-blue-100 border border-blue-200 rounded p-2">
                    <p className="text-xs text-blue-800">
                      <span className="font-semibold">Reviewer Note:</span> Verify source documents support these calculations and ensure methodology aligns with policy terms.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
